import { fetchWithWidgetToken, wait } from '../../helpers';
import { linkedinProfileAdapter, tiktokProfileAdapter } from '../adapters';
import type { SourceValue } from '../sources';

const adapters = [linkedinProfileAdapter, tiktokProfileAdapter];

function getProfileAdapter(source: SourceValue) {
  return adapters.find((adapter) => adapter.isApplicable(source));
}

interface GetAuthorOptions {
  retries?: number;
  retryDelayMs?: number;
}

const RETRYABLE_STATUSES = new Set([404, 425, 429, 500, 502, 503, 504]);

export async function getAuthor(
  source: SourceValue,
  options?: GetAuthorOptions
) {
  const { retries = 3, retryDelayMs = 1000 } = options ?? {};

  const adapter = getProfileAdapter(source);

  if (!adapter) {
    console.warn(`No adapter found for source: ${source?.platform}`);

    return undefined;
  }

  const url = adapter.buildUrl(source);

  const maxAttempts = Math.max(1, retries);

  const attemptFetch = async (attempt: number) => {
    try {
      const response = await fetchWithWidgetToken({ url });
      if (!response.ok) {
        const { status } = response;
        const shouldRetry =
          attempt < maxAttempts - 1 && RETRYABLE_STATUSES.has(status);

        if (shouldRetry) {
          console.warn(
            `Get Author: retryable status ${status} for ${
              source.platform
            }, attempt ${attempt + 1}/${maxAttempts}`
          );

          await wait(retryDelayMs);

          return attemptFetch(attempt + 1);
        }
        console.error(
          `Get Author: non-ok response (status ${status}) for ${source.platform} url: ${url}`
        );

        return undefined;
      }
      const data = await response.json();

      return adapter.mapResponse(data);
    } catch (err) {
      const isErrorInstance = err instanceof Error;
      const message = isErrorInstance ? err.message : String(err);
      const shouldRetry = attempt < maxAttempts - 1;

      if (shouldRetry) {
        console.warn(
          `Get Author: fetch error '${message}' for ${
            source.platform
          }, attempt ${attempt + 1}/${maxAttempts}`
        );

        await wait(retryDelayMs);

        return attemptFetch(attempt + 1);
      }

      if (isErrorInstance) {
        console.error(
          `Get Author: fatal error fetching author info for ${source.platform}: ${message}`
        );
      }

      return undefined;
    }
  };

  return attemptFetch(0);
}
